export default function CorporateGovernance() {
  return (
    <section id="governance" className="bg-white py-16 sm:py-24 px-5 sm:px-10 lg:px-16 border-t border-slate-100">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-10 sm:mb-14">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-emerald-500 rounded-full"></div>
            <span
              className="text-slate-500 text-xs font-bold tracking-[0.2em] uppercase"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              About BPI Holding SA
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 leading-[1.08] tracking-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Swiss Institutional
              <br />
              <span className="text-emerald-600">Governance.</span>
            </h2>
            <p className="text-slate-500 text-base leading-relaxed max-w-md" style={{ fontFamily: 'Inter, sans-serif' }}>
              BPI Holding SA operates under the strictest Swiss financial regulatory framework,
              delivering institutional-grade AML compliance services to the global digital asset industry.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 mb-6 sm:mb-8">
          {/* Left: About + Image */}
          <div className="rounded-xl sm:rounded-2xl overflow-hidden border border-slate-200">
            <div className="relative h-52 sm:h-64">
              <img
                src="https://readdy.ai/api/search-image?query=Geneva%20Switzerland%20financial%20district%20luxury%20corporate%20headquarters%20elegant%20stone%20masonry%20building%20facade%20golden%20hour%20dusk%20warm%20amber%20sunset%20light%20prestigious%20banking%20institution%20European%20architecture%20ornate%20stonework%20street%20view%20private%20wealth%20management%20headquarters%20Rue%20du%20Nant%20sophisticated%20premium%20upscale%20investment%20firm%20exterior&width=800&height=400&seq=bpi-gov-building-v4&orientation=landscape"
                alt="BPI Holding SA – Geneva, Rue du Nant"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#001529]/80 via-[#001529]/20 to-transparent"></div>
              <div className="absolute bottom-4 left-5 sm:bottom-5 sm:left-6 flex items-center gap-2.5">
                <div className="w-7 h-7 bg-white rounded-full flex items-center justify-center">
                  <span className="text-[10px] font-black text-slate-900">CH</span>
                </div>
                <span className="text-white text-sm font-semibold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Registered in Switzerland
                </span>
              </div>
            </div>

            <div className="p-6 sm:p-8">
              <h3
                className="text-slate-900 font-black text-xl sm:text-2xl mb-3"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                BPI Holding SA
              </h3>
              <p className="text-slate-500 text-sm sm:text-base leading-relaxed mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
                Established under Swiss law as a{' '}
                <strong className="text-slate-700 font-semibold">company limited by shares (AG)</strong>,
                BPI Holding SA is a Geneva-based investment operations firm specializing in high-risk
                digital asset assessment and institutional AML compliance. Our board focuses exclusively
                on high-level investment management, financial risk mitigation, and deep-tier verification
                of stablecoin infrastructure.
              </p>

              <div className="space-y-3">
                {[
                  { icon: 'ri-building-line', label: 'Legal Entity', value: 'BPI Holding SA — Aktiengesellschaft (AG)' },
                  { icon: 'ri-file-list-3-line', label: 'Register Number', value: 'CH-660.0.201.959-2' },
                  { icon: 'ri-map-pin-2-line', label: 'Registered Office', value: 'c/o Berney et Associés SA, Rue du Nant 8, 1207 Genève' },
                  { icon: 'ri-user-star-line', label: 'Board / Management', value: 'Ekaterina Butler — Management' },
                  { icon: 'ri-global-line', label: 'Jurisdiction', value: 'Switzerland — Canton of Geneva' },
                  { icon: 'ri-government-line', label: 'Regulatory Framework', value: 'Swiss FINMA / FATF Standards' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 border border-emerald-100 rounded-lg flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-emerald-600 text-sm`}></i>
                    </div>
                    <div>
                      <div className="text-slate-400 text-xs font-bold uppercase tracking-wider" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {item.label}
                      </div>
                      <div className="text-slate-800 text-sm font-medium mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {item.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Compliance + Services */}
          <div className="flex flex-col gap-4 sm:gap-5">

            {/* Regulatory Framework */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl p-5 sm:p-7">
              <h3 className="text-slate-900 font-black text-lg sm:text-xl mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Regulatory Compliance Framework
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Swiss FINMA', icon: 'ri-shield-star-line', color: 'text-emerald-600' },
                  { label: 'FATF Standards', icon: 'ri-global-line', color: 'text-emerald-600' },
                  { label: 'OFAC Compliance', icon: 'ri-flag-line', color: 'text-slate-600' },
                  { label: 'EU MiCA Aligned', icon: 'ri-europe-line', color: 'text-slate-600' },
                  { label: 'Travel Rule', icon: 'ri-route-line', color: 'text-slate-600' },
                  { label: 'ISO 27001', icon: 'ri-lock-password-line', color: 'text-slate-600' },
                ].map((item, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg p-3 sm:p-4 flex items-center gap-2">
                    <i className={`${item.icon} text-base ${item.color}`}></i>
                    <span className="text-slate-700 font-semibold text-xs" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Core Services */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl sm:rounded-2xl p-5 sm:p-7">
              <h3 className="text-slate-900 font-black text-lg sm:text-xl mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Core Institutional Services
              </h3>
              <div className="space-y-3">
                {[
                  'ERC-20 Smart Contract Deep Audit',
                  'BPI Verification Certificate Issuance',
                  'Wallet Provenance & Chain-of-Custody Reports',
                  'Real-Time Blacklist Monitoring',
                  'Institutional USDT/USDC Compliance Clearance',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-200 last:border-0">
                    <i className="ri-check-double-line text-emerald-500 text-base flex-shrink-0"></i>
                    <span className="text-slate-600 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Institutional CTA */}
            <div className="bg-[#001529] rounded-xl sm:rounded-2xl p-5 sm:p-7 flex items-start gap-4">
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-send-line text-lg text-emerald-400"></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold text-sm sm:text-base mb-0.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Institutional Inquiries
                </div>
                <div className="text-slate-400 text-xs sm:text-sm mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                  For partnerships, bulk verification, or compliance consulting — contact our Geneva team.
                </div>
                <a
                  href="https://mycryptoaml.com/verification"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-5 py-2.5 rounded-lg font-bold text-xs sm:text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-shield-check-line text-sm"></i>
                  Verify Your Assets Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
